/**
 * primitives — the shapes every Googie screen repeats.
 *
 * GOOGIE-1. Ten redesigned screens share one masthead, one stat tile,
 * one row, one tag. They live here so a change to the look is one
 * edit, not ten — and so a screen file reads as its own content
 * rather than as a re-transcription of the design canvas.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import orbitLogoWhite from '@/assets/orbit-retro-logo-white.png';
import orbitLogoBlack from '@/assets/orbit-retro-logo-black.png';
import { useGoogieSky } from './GoogieShell';
import { G } from '@/lib/googie';
import { cn } from '@/lib/utils';

/** The top bar: the Orbit mark on the left, a status capsule right. */
export function GoogieHeader({ capsule }: { capsule?: ReactNode }) {
  /* GOOGIE-CORNERS-1 — the capsule is GONE. Rennan crossed it out on
     two screens in one pass ("STUDIO · WAVESYNC", "LIBRARY · 31
     GALLERIES · 15 LIVE"), and he is right that it was saying nothing
     the page wasn't already: the masthead names the section a line
     below it, and the counts are in the masthead's own subtitle.

     Ignored here rather than removed from sixteen call sites, so the
     screens keep compiling and nobody has to re-thread a prop to get
     it back. It is deliberately still in the signature as the record
     of that decision. */
  void capsule;
  // GOOGIE-9 — the wordmark follows the ground it's printed on. This
  // was hard-coded to the white logotype, which is a white mark on
  // cream paper the moment a studio picks light mode.
  const { light, railed } = useGoogieSky();
  /* With the capsule gone, a railed screen has nothing left to draw —
     the side rail carries the wordmark. Rendering the element anyway
     left an empty strip of pt-7/pt-9 above every masthead, which is
     most of why "the UI is too low". Nothing to show, nothing to
     render, and the page starts where the page starts. */
  if (railed) return null;
  return (
    <div
      className={`relative mx-auto flex w-full max-w-[2100px] items-center px-5 pt-7 sm:px-8 sm:pt-9 md:px-12 lg:px-16 2xl:px-24 ${
        railed ? 'justify-end' : 'justify-between'
      }`}
    >
      {/* GOOGIE-3 — the mark at the size Rennan asked for. The canvas
          draws it small because a canvas board is a picture of a
          screen; on a real 27-inch display it read as a footnote.

          GOOGIE-16 — but not when the side rail is up. The rail puts
          the wordmark at its own top, so drawing it here too gave
          sidebar studios two Orbit logos an inch apart. The rail
          keeps it; the capsule slides right on its own. */}
      {!railed && (
        <Link to="/" aria-label="Today">
          <img
            src={light ? orbitLogoBlack : orbitLogoWhite}
            alt="Orbit"
            className="block h-[34px] w-auto sm:h-[46px] md:h-[54px]"
          />
        </Link>
      )}
      {capsule}
    </div>
  );
}

/** The status capsule — "30 INQUIRIES · 11 IDLE". */
export function GoogieCapsule({ dot, children }: { dot?: string; children: ReactNode }) {
  return (
    <span className="g-capsule">
      {dot && (
        <span
          aria-hidden
          style={{ width: 10, height: 10, borderRadius: '50%', background: dot, boxShadow: `0 0 10px ${dot}` }}
        />
      )}
      {children}
    </span>
  );
}

/** Eyebrow + big Bricolage headline + optional right-hand controls. */
export function GoogieMasthead({
  eyebrow,
  eyebrowColor,
  title,
  sub,
  actions,
}: {
  eyebrow?: string;
  eyebrowColor?: string;
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    /* GOOGIE-CORNERS-1 — gutters matched to GoogiePage, and the top
       padding halved: this used to sit under GoogieHeader's own
       pt-7/pt-9, and with that gone the two were stacking into a very
       long drop before the first word on the page. */
    <div className="g-rise mx-auto flex w-full max-w-[2100px] flex-wrap items-end justify-between gap-5 px-5 pt-6 sm:gap-6 sm:px-8 sm:pt-7 md:px-10 lg:px-10 2xl:px-14">
      <div className="min-w-0">
        {eyebrow && (
          <div className="g-eyebrow" style={eyebrowColor ? { color: eyebrowColor } : undefined}>
            {eyebrow}
          </div>
        )}
        <h1 className="g-h1 mt-3">{title}</h1>
        {sub && <p className="g-mute mt-4 max-w-[62ch] text-[17.5px] leading-relaxed">{sub}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </div>
  );
}

/** One stat tile — number over label, optionally filled in an accent. */
export function GoogieStat({
  value,
  label,
  icon,
  accent,
  filled = false,
  tone,
  to,
}: {
  value: ReactNode;
  label: ReactNode;
  icon?: ReactNode;
  /** The tile's hue — the badge, the number, and the ground. */
  accent?: string;
  /**
   * Weight this tile above its neighbours — the one alarming stat.
   *
   * GOOGIE-TONE-1 — this used to paint the whole tile in the accent
   * at full saturation with ivory type, which is what Rennan meant by
   * "one tile shouts, three whisper": the loud one wasn't a louder
   * version of the others, it was a different design. Now it is the
   * same tile with a stronger ground and a visible edge.
   */
  filled?: boolean;
  /** Override the hue, when a screen has already picked one. */
  tone?: string;
  /**
   * Where the tile goes when tapped.
   *
   * GOOGIE-13 — every one of these numbers is a question with an
   * obvious next screen ("11 idle leads" → the leads), and a number
   * you can't act on is a poster. The Pipeline tiles below already
   * behaved this way; these didn't, which made half the dashboard
   * feel live and half of it feel printed.
   */
  to?: string;
}) {
  /* GOOGIE-TONE-1 — the tile's entire colour decision is one custom
     property. googie.css derives the ground, the number and the edge
     from it, pinning the hue's LIGHTNESS so the number is legible on
     glass over any of the four skies and in either theme. What used
     to be here was a hand-mixed inline block that only worked on one
     of them. `tone` feeds the same recipe rather than bypassing it:
     Projects and Inquiries pass a raw hue, which measured 1.0:1
     against the dawn sky. */
  const hue = tone ?? accent ?? G.teal;
  const style = { '--g-stat-tone': hue } as CSSProperties;
  const body = (
    <>
      {icon && (
        <span className="g-badge" style={{ background: `${accent ?? hue}33`, outlineColor: 'var(--g-track)' }}>
          {icon}
        </span>
      )}
      <div className="g-display text-[39.5px]">{value}</div>
      <div className="text-[15px]" style={{ color: 'var(--g-mute)' }}>
        {label}
      </div>
    </>
  );

  const shape = cn(
    'g-stat g-card-sm flex min-w-[150px] flex-1 flex-col gap-3 p-5',
    filled && 'g-stat-filled',
  );

  if (!to) return <div className={shape} style={style}>{body}</div>;

  return (
    <Link
      to={to}
      className={`${shape} no-underline transition-transform duration-300 hover:-translate-y-1`}
      style={{ color: 'inherit', ...style }}
    >
      {body}
    </Link>
  );
}

/**
 * A round initial disc — every list of people uses it.
 *
 * GOOGIE-TONE-1 — the colour moved to `.g-mark`. This was
 * `background: ${hue}33, color: hue`: a 20% wash of a hue with the
 * same hue on top of it, which on the dawn and dusk skies measured
 * about 1:1. The initial was decoration pretending to be a letter.
 */
export function GoogieAvatar({ name, hue, size = 36 }: { name: string; hue?: string; size?: number }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <span
      aria-hidden
      className="g-mark grid flex-none place-items-center rounded-full font-semibold"
      style={{ '--g-chip-tone': hue ?? G.sky, width: size, height: size, fontSize: size * 0.4 } as CSSProperties}
    >
      {initial}
    </span>
  );
}

/**
 * Status tag — Pending / Accepted / Delivered / Failed.
 *
 * GOOGIE-TONE-1 — now a `g-chip`. It used to be a solid block of the
 * hue with `--g-on-bright` (always ink) on top, which is fine on
 * mustard and lime and 2.3:1 on violet. One recipe, nine hues, no
 * per-colour judgement calls.
 */
export function GoogieTag({ children, color }: { children: ReactNode; color: string }) {
  return <GoogieChip tone={color}>{children}</GoogieChip>;
}

/**
 * A state, in its own colour — the shape every list, row and header
 * reaches for to say "overdue", "delivered", "waiting".
 *
 * GOOGIE-TONE-1. Nine screens hand-rolled this as
 * `background: ${tone}26, color: tone`, a 15% wash of a hue with the
 * same hue as type, which measured as low as 1.01:1 on the glass it
 * sits on. Pass the hue; `.g-chip` in googie.css derives a pale
 * ground, deep type and a mid-lightness edge from it, and the result
 * clears AA under every sky, season and theme. No `tone` at all is a
 * deliberate neutral — a draft is not a state worth a colour.
 */
export function GoogieChip({
  children,
  tone,
  className,
}: {
  children: ReactNode;
  tone?: string;
  className?: string;
}) {
  if (!tone) return <span className={cn('g-chip g-chip-mute', className)}>{children}</span>;
  return (
    <span className={cn('g-chip', className)} style={{ '--g-chip-tone': tone } as CSSProperties}>
      {children}
    </span>
  );
}

/** A section heading inside a screen — "Active projects 5". */
export function GoogieSectionTitle({ children, count, action }: { children: ReactNode; count?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-baseline justify-between gap-4">
      <h2 className="g-h2">
        {children}
        {count !== undefined && <span className="g-mute ml-2 font-normal">{count}</span>}
      </h2>
      {action}
    </div>
  );
}

/** The body column every screen's content sits in. */
export function GoogiePage({ children, className }: { children: ReactNode; className?: string }) {
  /* GOOGIE-6 — "it's just in the center instead of evenly spread out
     across the pages." GOOGIE-5 widened the column to 1320 and that
     was still a column: on a 1780-wide window it left 230px of empty
     sky down each side and the whole app read as a strip.
     
     So there is no column any more. The page is the display, with
     gutters that grow with it, and a ceiling only at 2100 — past
     that a line of body copy stops being readable and the limit is
     doing real work rather than centring for its own sake. */
  return (
    /* GOOGIE-CORNERS-1 — the large-screen gutters came down (lg 64→40,
       2xl 96→56). They were set when this page had the whole window;
       with the side rail up there is already 264px of chrome on the
       left, and 96px of sky on top of that pushed the content into a
       strip in the middle of the display. Rennan: "needs to be pushed
       to the corners with appropriate spacing." */
    <div
      className={`relative mx-auto w-full max-w-[2100px] px-5 pb-8 sm:px-8 md:px-10 lg:px-10 2xl:px-14 ${className ?? ''}`}
    >
      {children}
    </div>
  );
}

/** A loading placeholder in the redesign's own language. */
export function GoogieLoading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <span className="g-mute text-[16px]">{label}</span>
    </div>
  );
}

/** Nothing here yet. */
export function GoogieEmpty({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="g-card flex flex-col items-center gap-3 px-8 py-14 text-center">
      <h3 className="g-h3">{title}</h3>
      {body && <p className="g-mute max-w-[46ch] text-[16px] leading-relaxed">{body}</p>}
      {action}
    </div>
  );
}
